import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { QuoteStepName, QuoteStepState, QuoteStepStatus } from '../../services/quote-step.service';

@Component({
    standalone: false,
    selector: 'por-quote-step-tracker',
    templateUrl: './quote-step-tracker.component.html',
    styleUrls: ['./quote-step-tracker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuoteStepTrackerComponent {
    private readonly featureToggleService = inject(FeatureToggleService);

    @Input() status!: QuoteStepStatus;
    @Input() autoConvertEnabled = false;
    // True only once the contract is actually a Reservation; all steps done on a still-Quote contract (a timed-out
    // or rejected conversion) must not read as a completed reservation.
    @Input() converted = false;
    @Input() storeName: string | null | undefined;
    // The QuickLink page already shows its own incomplete-steps banner above the eSign/Payment buttons.
    @Input() hideWarning = false;
    // The eSign step button only appears on QuickLink. Payment's button lives in the Payment Information
    // accordion (contract-detail), so the tracker doesn't render one.
    @Input() showEsignButton = false;
    @Output() readonly goToStep = new EventEmitter<QuoteStepName>();

    // AC10: with nothing required, there's no conversion to explain, so the CTA button (not the step
    // buttons - those stay rendered whenever the host *ngIf in contract-detail matches) only shows
    // when auto-convert is on (to explain that state) or a step is actually required.
    get showCta(): boolean {
        return this.ctaAvailable && (this.autoConvertEnabled || this.status.hasAnyRequiredStep);
    }

    // POR force-hides the CTA and its warning org-wide while Elite ties payments to the customer, not the quote.
    get ctaAvailable(): boolean {
        return this.featureToggleService.isAvailable('completeReservationButton');
    }

    get allComplete(): boolean {
        return this.status.allRequiredStepsComplete;
    }

    // True only when a required step was actually completed - as opposed to allComplete being
    // vacuously true because nothing was required at all (AC10). shouldAutoConvert never fires for the
    // vacuous case (see quote-step.service.ts), so labels/warnings must not claim a completion that
    // will never actually happen.
    private get hasGenuineCompletion(): boolean {
        return this.allComplete && this.status.hasAnyRequiredStep;
    }

    // AC10: neutral marker on the action button when nothing is required (nothing to complete or explain).
    get ctaGlyph(): string {
        return this.status.hasAnyRequiredStep ? '' : '⊝ ';
    }

    // AC6/AC10: ✓ for a completed step, ⊝ for a not-required one.
    stepGlyph(state: QuoteStepState): string {
        if (state === 'complete') {
            return '✓ ';
        }
        return state === 'notRequired' ? '⊝ ' : '';
    }

    // AC1/AC2/AC3/AC4 label matrix.
    get buttonLabelKey(): string {
        if (this.autoConvertEnabled) {
            return this.hasGenuineCompletion && this.converted ? 'ReservationComplete' : 'CompleteYourReservation';
        }
        return this.hasGenuineCompletion ? 'QuoteComplete' : 'CompleteYourQuote';
    }

    get buttonDisabled(): boolean {
        return this.allComplete;
    }

    // Red text above the button. AC1/AC3 share the same incomplete-steps warning; AC2 has none; AC4 has
    // the store-will-reach-out message.
    get warningKey(): string | null {
        if (!this.ctaAvailable) {
            return null;
        }
        if (!this.allComplete) {
            return 'IncompleteStepsWarning';
        }
        if (this.autoConvertEnabled || !this.status.hasAnyRequiredStep) {
            return null;
        }
        // WHY: storeName comes from an async config fetch that may not have resolved yet when this first
        // renders (or could stay unset if that call fails); interpolating it unconditionally would render
        // the literal word "undefined" in this red, always-visible banner instead of a real store name.
        // HOW: only surface the AC4 message once there's an actual name to substitute in it — until then,
        // suppress the warning rather than show a broken sentence promising a callback with no identity.
        return this.storeName ? 'QuoteCompleteStoreWillReachOut' : null;
    }

    // AC5: routes to the next incomplete required step (Payment, then eSign). AC7: this is just one of
    // several independent entry points into a step — clicking a step button directly (onStepClick) works too.
    onCtaClick(): void {
        const next = this.status.firstIncompleteRequiredStep;
        if (next) {
            this.goToStep.emit(next);
        }
    }

    onStepClick(step: QuoteStepName, state: QuoteStepStatus['payment']): void {
        if (state === 'incomplete') {
            this.goToStep.emit(step);
        }
    }
}
